<template>
  <span>
    <v-btn
      v-if="$vuetify.breakpoint.xsOnly"
      icon
      color="primary"
      aria-label="Diagnostic RLR Rating"
      @click="open"
    >
      <v-icon>mdi-chart-box-outline</v-icon>
    </v-btn>
    <v-btn v-else outlined color="primary" @click="open">
      <v-icon left>mdi-chart-box-outline</v-icon>
      Diagnostic RLR
    </v-btn>

    <v-dialog v-model="dialog" max-width="1100" scrollable>
      <v-card>
        <v-card-title class="text-h5">
          <v-icon left color="primary">mdi-chart-box-outline</v-icon>
          Diagnostic RLR Rating
          <v-spacer />
          <v-btn icon aria-label="Închide" @click="dialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-divider />

        <v-card-text class="pa-4 pa-sm-6">
          <v-skeleton-loader
            v-if="loading"
            type="heading, card, card, table-heading, table-thead, table-tbody"
          />
          <v-alert v-else-if="error" type="error" outlined>
            {{ error }}
            <v-btn text color="error" class="ml-2" @click="load">Reîncearcă</v-btn>
          </v-alert>
          <p v-else-if="!groups.length" class="text--secondary mb-0">
            Nu există încă date RLR Rating pentru diagnostic.
          </p>
          <template v-else>
            <v-alert type="info" text dense>
              Indicatorii sunt informativi și nu modifică ratingurile. Piloții activi sunt
              cei care au participat la cel puțin o cursă clasată în ultimele 12 luni.
            </v-alert>

            <v-tabs v-model="activeGroup" show-arrows class="mb-4">
              <v-tab v-for="group in groups" :key="group.simulator._id">
                {{ group.simulator.acronym || group.simulator.title }}
              </v-tab>
            </v-tabs>

            <template v-if="currentGroup">
              <v-alert v-if="policySuccess" type="success" outlined dismissible @input="policySuccess = ''">
                {{ policySuccess }}
              </v-alert>
              <v-alert
                v-if="currentGroup.rebuildState && currentGroup.rebuildState.status === 'failed'"
                type="error"
                outlined
              >
                <div class="d-flex flex-column flex-sm-row align-sm-center">
                  <div>
                    Ultima recalculare a eșuat. Clasamentul anterior complet este în continuare activ.
                    <div v-if="currentGroup.rebuildState.error" class="text-caption mt-1">
                      {{ currentGroup.rebuildState.error }}
                    </div>
                  </div>
                  <v-spacer />
                  <v-btn
                    color="error"
                    text
                    :loading="rebuildRetrying"
                    class="mt-2 mt-sm-0"
                    @click="retryRebuild"
                  >
                    Recalculează
                  </v-btn>
                </div>
              </v-alert>
              <v-alert
                v-else-if="currentGroup.rebuildState && currentGroup.rebuildState.status === 'rebuilding'"
                type="info"
                text
              >
                RLR Rating se recalculează. Clasamentul anterior rămâne vizibil până la finalizare.
              </v-alert>

              <v-card outlined class="mb-5">
                <v-card-title class="text-subtitle-1 font-weight-bold">
                  <v-icon left color="primary">mdi-tune-variant</v-icon>
                  Politică RLR Rating — {{ currentGroup.simulator.title }}
                </v-card-title>
                <v-card-text v-if="currentPolicyDraft">
                  <v-radio-group
                    v-model="currentPolicyDraft.participationMode"
                    :disabled="policySaving"
                    row
                    class="mt-0"
                  >
                    <v-radio label="Bonus fix" value="fixed" />
                    <v-radio label="Calcul dinamic" value="dynamic" />
                  </v-radio-group>

                  <v-text-field
                    v-if="currentPolicyDraft.participationMode === 'fixed'"
                    v-model.number="currentPolicyDraft.fixedBonus"
                    type="number"
                    min="0"
                    step="1"
                    outlined
                    dense
                    label="Bonus per pilot eligibil"
                    :disabled="policySaving"
                    :rules="[wholeNumberRule]"
                    class="policy-bonus-input"
                  />

                  <v-alert
                    v-if="currentPolicyDraft.participationMode === 'fixed'"
                    type="info"
                    text
                    dense
                    class="mb-0"
                  >
                    Recomandare pentru {{ currentGroup.simulator.acronym || currentGroup.simulator.title }}:
                    <strong>+{{ currentGroup.recommendedFixedBonus }}</strong>, pe baza balanței istorice
                    a modificărilor de performanță.
                  </v-alert>
                  <v-alert v-else type="info" text dense class="mb-0">
                    Bonusul este calculat pentru fiecare cursă și clasă:
                    <code>max(0, round(-balanță / piloți eligibili))</code>.
                    Nu poate fi negativ. Media istorică indicativă este
                    <strong>+{{ currentGroup.dynamicIndicativeAverage }}</strong>, cu un maxim de
                    <strong>+{{ currentGroup.dynamicIndicativeMaximum }}</strong>.
                  </v-alert>

                  <v-divider class="my-5" />
                  <div class="text-subtitle-2 font-weight-bold mb-1">Factor K pentru piloții noi</div>
                  <div class="text-caption text--secondary mb-2">
                    Se aplică primelor 5 curse eligibile ale fiecărui pilot.
                  </div>
                  <v-radio-group
                    v-model="currentPolicyDraft.provisionalKMode"
                    :disabled="policySaving"
                    class="mt-0 mb-0"
                  >
                    <v-radio label="Același K pentru toți — K 200" value="standard" />
                    <v-radio label="K dublu pentru primele 5 curse — K 400" value="double" />
                  </v-radio-group>
                  <v-alert type="info" text dense class="mb-0">
                    K 400 calibrează mai repede piloții noi, dar poate produce inflație sau
                    deflație. K 200 păstrează schimburile mai aproape de zero-sum.
                  </v-alert>
                </v-card-text>
                <v-card-actions>
                  <span v-if="policyDirty" class="text-caption warning--text ml-2">
                    Modificări nesalvate
                  </span>
                  <v-spacer />
                  <v-btn
                    color="primary"
                    text
                    :disabled="!policyDirty || !policyValid || policySaving"
                    :loading="policySaving"
                    @click="confirmationDialog = true"
                  >
                    Salvează și recalculează
                  </v-btn>
                </v-card-actions>
              </v-card>

              <v-row dense>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Piloți activi"
                    :value="`${currentGroup.activeDrivers} / ${currentGroup.totalDrivers}`"
                    icon="mdi-account-check"
                    hint="Piloți cu cel puțin o cursă Ranked în ultimele 12 luni, raportat la totalul piloților cu rating."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Media activă"
                    :value="currentGroup.activeDrivers ? currentGroup.activePoolAverage : '—'"
                    icon="mdi-account-group"
                    hint="Ratingul mediu al piloților activi în ultimele 12 luni."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Media all-time"
                    :value="currentGroup.allTimePoolAverage"
                    icon="mdi-scale-balance"
                    hint="Ratingul mediu al tuturor piloților care au un rating în acest simulator, inclusiv cei inactivi."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Balanță față de start"
                    :value="signedValue(currentGroup.ratingPointBalance)"
                    icon="mdi-bank-transfer"
                    hint="Diferența dintre totalul ratingurilor curente și totalul inițial de 2500 de puncte per pilot."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Net / cursă"
                    :value="signedValue(currentGroup.netPointsPerRace)"
                    icon="mdi-swap-vertical"
                    hint="Numărul mediu de puncte create sau pierdute de sistem la fiecare cursă Ranked."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Net / ultimele 20"
                    :value="signedValue(currentGroup.recentNetPointsPerRace)"
                    icon="mdi-history"
                    hint="Balanța medie de puncte per cursă, calculată numai pentru ultimele 20 de curse Ranked."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Variatie mediană"
                    :value="currentGroup.medianEstablishedChange"
                    icon="mdi-chart-bell-curve"
                    hint="Valoarea mediană absolută a schimbării de rating pentru piloții cu peste 5 curse eligibile."
                  />
                </v-col>
                <v-col cols="6" md="3">
                  <metric-card
                    label="Ultima cursă"
                    :value="formatDate(currentGroup.latestRatedAt)"
                    icon="mdi-calendar-clock"
                    hint="Data celei mai recente curse incluse în calculul RLR Rating."
                  />
                </v-col>
              </v-row>

              <v-row dense class="mt-1">
                <v-col cols="12" sm="6">
                  <v-card outlined height="100%">
                    <v-card-text>
                      <div class="text-caption text--secondary">Cel mai mare rating</div>
                      <div v-if="currentGroup.highest" class="d-flex align-center justify-space-between">
                        <strong>{{ currentGroup.highest.driver }}</strong>
                        <span class="text-h6 primary--text">{{ currentGroup.highest.rating }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
                <v-col cols="12" sm="6">
                  <v-card outlined height="100%">
                    <v-card-text>
                      <div class="text-caption text--secondary">Cel mai mic rating</div>
                      <div v-if="currentGroup.lowest" class="d-flex align-center justify-space-between">
                        <strong>{{ currentGroup.lowest.driver }}</strong>
                        <span class="text-h6 primary--text">{{ currentGroup.lowest.rating }}</span>
                      </div>
                    </v-card-text>
                  </v-card>
                </v-col>
              </v-row>

              <div class="d-flex flex-column flex-sm-row justify-space-between mt-6 mb-2">
                <h3 class="text-h6">Evoluție anuală</h3>
                <span class="text-caption text--secondary">
                  {{ currentGroup.races }} curse · {{ currentGroup.ledgerEntries }} modificări în registru
                </span>
              </div>
              <v-data-table
                :headers="yearHeaders"
                :items="currentGroup.years"
                disable-pagination
                hide-default-footer
                dense
              >
                <template v-slot:[`item.netPoints`]="{ item }">
                  <span :class="netValueClass(item.netPoints)">{{ signedValue(item.netPoints) }}</span>
                </template>
              </v-data-table>
            </template>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions>
          <v-btn v-if="groups.length" text color="primary" :loading="loading" @click="load">
            <v-icon left>mdi-refresh</v-icon>
            Reîncarcă
          </v-btn>
          <v-spacer />
          <v-btn text @click="dialog = false">Închide</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="confirmationDialog" max-width="650" persistent>
      <v-card v-if="currentGroup && currentPolicyDraft">
        <v-card-title class="text-h5">Confirmă recalcularea</v-card-title>
        <v-card-text>
          <p>
            Politica de participare pentru <strong>{{ currentGroup.simulator.title }}</strong>
            va fi schimbată din <strong>{{ policyLabel(currentGroup.policy) }}</strong> în
            <strong>{{ policyLabel(currentPolicyDraft) }}</strong>.
          </p>
          <p>
            Factor K: <strong>{{ kFactorLabel(currentGroup.policy) }}</strong> →
            <strong>{{ kFactorLabel(currentPolicyDraft) }}</strong>.
          </p>
          <v-alert type="warning" text>
            Toate cursele Ranked ale acestui simulator vor fi recalculate retroactiv,
            de la prima cursă eligibilă. Celelalte simulatoare nu sunt afectate.
          </v-alert>
          <v-simple-table dense>
            <tbody>
              <tr><td>Simulator</td><td class="text-right font-weight-bold">{{ currentGroup.simulator.title }}</td></tr>
              <tr><td>Sezoane Ranked</td><td class="text-right">{{ currentGroup.rankedSeasons }}</td></tr>
              <tr><td>Curse</td><td class="text-right">{{ currentGroup.races }}</td></tr>
              <tr><td>Piloți</td><td class="text-right">{{ currentGroup.totalDrivers }}</td></tr>
              <tr><td>Modificări istorice</td><td class="text-right">{{ currentGroup.ledgerEntries }}</td></tr>
            </tbody>
          </v-simple-table>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text :disabled="policySaving" @click="confirmationDialog = false">Anulează</v-btn>
          <v-btn color="primary" :loading="policySaving" @click="savePolicy">
            Confirmă și recalculează {{ currentGroup.simulator.acronym || currentGroup.simulator.title }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import axios from '@/plugins/axios'
import MetricCard from '@/components/RlrRatingMetricCard'

export default {
  name: 'RlrRatingDiagnostics',
  components: {MetricCard},
  data: () => ({
    dialog: false,
    loading: false,
    error: '',
    groups: [],
    activeGroup: 0,
    requestedSimulatorId: '',
    policyDrafts: {},
    policySaving: false,
    rebuildRetrying: false,
    policySuccess: '',
    confirmationDialog: false,
    yearHeaders: [
      {text: 'An', value: 'year'},
      {text: 'Piloți noi', value: 'newDrivers', align: 'end'},
      {text: 'Piloți participanți', value: 'activeDrivers', align: 'end'},
      {text: 'Curse', value: 'races', align: 'end'},
      {text: 'Balanță puncte', value: 'netPoints', align: 'end'}
    ]
  }),
  computed: {
    currentGroup() {
      return this.groups[this.activeGroup] || null
    },
    currentPolicyDraft() {
      return this.currentGroup ? this.policyDrafts[this.currentGroup.simulator._id] : null
    },
    policyDirty() {
      if (!this.currentGroup || !this.currentPolicyDraft) return false
      return this.serializePolicy(this.currentGroup.policy) !== this.serializePolicy(this.currentPolicyDraft)
    },
    policyValid() {
      if (!this.currentPolicyDraft) return false
      const bonus = Number(this.currentPolicyDraft.fixedBonus)
      return Number.isInteger(bonus) && bonus >= 0 &&
        ['standard', 'double'].includes(this.currentPolicyDraft.provisionalKMode)
    }
  },
  methods: {
    async retryRebuild() {
      if (!this.currentGroup || this.rebuildRetrying) return
      const simulatorId = this.currentGroup.simulator._id
      this.rebuildRetrying = true
      this.error = ''
      try {
        const response = await axios.post(`/api/rlr-rating-rebuild/${simulatorId}`)
        const rebuild = response.data.data.rebuild
        this.policySuccess = `Recalcularea a reușit: ${rebuild.drivers} piloți și ${rebuild.changes} modificări.`
        this.requestedSimulatorId = simulatorId
        await this.load()
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.error
        this.error = message || 'Recalcularea RLR Rating a eșuat din nou.'
        await this.load()
      } finally {
        this.rebuildRetrying = false
      }
    },
    async open(simulatorId) {
      this.requestedSimulatorId = simulatorId || ''
      this.dialog = true
      if (!this.groups.length) {
        await this.load()
      } else {
        this.selectRequestedSimulator()
      }
    },
    async load() {
      this.loading = true
      this.error = ''
      try {
        const selectedSimulatorId = this.requestedSimulatorId
          || (this.currentGroup && this.currentGroup.simulator._id)
        const response = await axios.get('/api/rlr-rating-diagnostics')
        this.groups = response.data.data || []
        this.policyDrafts = {}
        this.groups.forEach(group => {
          this.$set(this.policyDrafts, group.simulator._id, Object.assign(
            {participationMode: 'fixed', fixedBonus: 0, provisionalKMode: 'double'},
            group.policy || {}
          ))
        })
        this.requestedSimulatorId = selectedSimulatorId || ''
        this.selectRequestedSimulator()
      } catch (error) {
        this.error = 'Datele de diagnostic RLR Rating nu au putut fi încărcate.'
      } finally {
        this.loading = false
      }
    },
    selectRequestedSimulator() {
      const index = this.groups.findIndex(group => group.simulator._id === this.requestedSimulatorId)
      this.activeGroup = index >= 0 ? index : Math.min(this.activeGroup, Math.max(this.groups.length - 1, 0))
    },
    serializePolicy(policy) {
      const supplied = policy || {}
      return JSON.stringify({
        participationMode: supplied.participationMode === 'dynamic' ? 'dynamic' : 'fixed',
        fixedBonus: Number(supplied.fixedBonus) || 0,
        provisionalKMode: supplied.provisionalKMode === 'standard' ? 'standard' : 'double'
      })
    },
    policyLabel(policy) {
      if (policy && policy.participationMode === 'dynamic') return 'calcul dinamic, minimum 0'
      return `bonus fix +${Number(policy && policy.fixedBonus) || 0}`
    },
    kFactorLabel(policy) {
      return policy && policy.provisionalKMode === 'standard'
        ? 'K 200 pentru toți'
        : 'K 400 în primele 5 curse'
    },
    wholeNumberRule(value) {
      return (Number.isInteger(Number(value)) && Number(value) >= 0)
        || 'Introdu un număr întreg pozitiv sau 0'
    },
    async savePolicy() {
      if (!this.currentGroup || !this.policyValid || !this.policyDirty || this.policySaving) return
      const simulatorId = this.currentGroup.simulator._id
      const simulatorName = this.currentGroup.simulator.title
      this.policySaving = true
      try {
        const response = await axios.post(
          `/api/rlr-rating-policy/${simulatorId}`,
          this.currentPolicyDraft
        )
        const rebuild = response.data.data.rebuild
        this.confirmationDialog = false
        this.policySuccess = `Politica ${simulatorName} a fost salvată. ${rebuild.drivers} piloți, ${rebuild.changes} modificări și ${rebuild.participationPoints} puncte de participare au fost recalculate.`
        this.requestedSimulatorId = simulatorId
        await this.load()
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.error
        this.error = message || 'Politica de participare nu a putut fi salvată.'
        this.confirmationDialog = false
      } finally {
        this.policySaving = false
      }
    },
    signedValue(value) {
      return Number(value) > 0 ? `+${value}` : `${value}`
    },
    netValueClass(value) {
      if (value > 0) return 'success--text font-weight-bold'
      if (value < 0) return 'error--text font-weight-bold'
      return ''
    },
    formatDate(value) {
      if (!value) return '—'
      const date = new Date(value)
      const pad = number => String(number).padStart(2, '0')
      return `${pad(date.getDate())}.${pad(date.getMonth() + 1)}.${date.getFullYear()}`
    }
  }
}
</script>

<style scoped>
.policy-bonus-input {
  max-width: 280px;
}
</style>
